import { useCallback, useState, useSyncExternalStore } from 'react';
import { assinarConvite, ehIOS, estaInstalado, instalar, temConvite } from '../lib/pwa';
import { Alerta, Botao, Modal } from './Interface';

/**
 * Convite para instalar o sistema como app.
 *
 * Tres caminhos, porque os navegadores nao concordam: Android e desktop tem o
 * convite nativo; iPhone so instala pelo Compartilhar; e o resto instala pelo
 * menu do navegador. Quem ja instalou nao ve nada — botao que nao faz nada e
 * pior do que botao nenhum.
 */
export default function InstalarApp({ compacto = false }: { compacto?: boolean }) {
  const convite = useSyncExternalStore(assinarConvite, temConvite, () => false);
  const [aberto, setAberto] = useState(false);
  const [ocupado, setOcupado] = useState(false);
  const [erro, setErro] = useState<string | null>(null);

  // instalado nao muda durante a visita: quem instala sai desta janela para o app
  const instalado = estaInstalado();
  const ios = ehIOS();

  const abrir = useCallback(async () => {
    setErro(null);
    // no Android e no desktop o convite nativo e melhor do que qualquer tela
    // nossa: um toque e acabou. So caimos no passo a passo quando ele nao existe.
    if (convite) {
      setOcupado(true);
      try {
        const escolha = await instalar();
        if (escolha === 'sem_convite') setAberto(true);
      } catch {
        setErro('O navegador não abriu a instalação. Dá para instalar pelo menu dele.');
        setAberto(true);
      } finally {
        setOcupado(false);
      }
      return;
    }
    setAberto(true);
  }, [convite]);

  if (instalado) return null;

  return (
    <>
      {compacto ? (
        <button
          onClick={() => void abrir()}
          aria-label="Instalar o app"
          title="Instalar o app"
          className="flex h-11 w-11 items-center justify-center rounded-xl text-white/60 transition hover:bg-white/10 hover:text-white active:scale-95"
        >
          <IconeBaixar />
        </button>
      ) : (
        <button
          onClick={() => void abrir()}
          disabled={ocupado}
          className="flex w-full items-center justify-center gap-2 rounded-xl border border-white/40 bg-white/10 py-2 text-sm font-medium text-white transition hover:bg-white/20 active:scale-[0.98] disabled:opacity-60"
        >
          <IconeBaixar />
          {ocupado ? 'Abrindo...' : 'Instalar o app'}
        </button>
      )}

      {aberto && (
        <Modal titulo="Instalar o app" aoFechar={() => setAberto(false)}>
          {erro && <Alerta>{erro}</Alerta>}

          <p className="mt-3 text-sm text-tinta-600">
            Instalado, o sistema abre direto pelo ícone, sem a barra do navegador, e o que você já
            viu continua aparecendo mesmo com a internet ruim.
          </p>

          {ios ? (
            <ol className="mt-4 space-y-3">
              {[
                'Toque no botão Compartilhar, na barra de baixo do Safari.',
                'Role a lista e escolha "Adicionar à Tela de Início".',
                'Confirme em "Adicionar". O ícone aparece junto dos seus apps.',
              ].map((passo, i) => (
                <li key={passo} className="flex gap-3 text-sm text-tinta-700">
                  <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-tinta-900 text-xs font-semibold text-white">
                    {i + 1}
                  </span>
                  <span className="pt-0.5">{passo}</span>
                </li>
              ))}
            </ol>
          ) : (
            <p className="mt-4 rounded-xl bg-tinta-100 px-3 py-2.5 text-sm text-tinta-700">
              Abra o menu do navegador e escolha <strong>Instalar</strong> ou{' '}
              <strong>Adicionar à tela inicial</strong>. Se a opção não aparecer, seu navegador ainda
              não instala apps: no celular, o Chrome e o Safari instalam.
            </p>
          )}

          <div className="mt-5 flex justify-end">
            <Botao variante="secundario" onClick={() => setAberto(false)}>
              Entendi
            </Botao>
          </div>
        </Modal>
      )}
    </>
  );
}

/** Seta entrando numa base: o mesmo traco dos outros icones do sistema. */
function IconeBaixar() {
  return (
    <svg
      viewBox="0 0 24 24"
      className="h-5 w-5 shrink-0"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.8}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M12 4v10" />
      <path d="m8 11 4 4 4-4" />
      <path d="M5 18h14" />
    </svg>
  );
}
